import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SquareRoundedIcon from '@mui/icons-material/SquareRounded';
import {
  Box,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
  Popper,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import GridItem from 'components/GridItem';
import { PROJECT_MODAL, projectModalActions } from 'features/Project/projectModalSlice';
import { useLazyGetProjectByIdQuery } from 'services/busmanApi';
import { ProjectResponse } from 'types/ProjectTypes';
import { getRAGStatus } from 'utils/project';
import { mapProjectResponseToRequest } from 'utils/project';

import { BorderLinearProgress, ProjectItemGrid } from '../styles';
interface ProjectLineItemProps {
  projectData: ProjectResponse;
}

const ProjectLineItem = ({ projectData }: ProjectLineItemProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [getProjectById] = useLazyGetProjectByIdQuery();
  const {
    id: projectId,
    name: projectTitle,
    start_date: startDate,
    finish_date: finishDate,
    estimated_cost: projectCost,
    status_display: projectStatus,
    client_contact_name: clientName,
    percent_complete: progressPercent,
  } = projectData;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent) => {
    setAnchorEl(anchorEl ? null : (event.currentTarget as HTMLElement));
  };
  const open = Boolean(anchorEl);
  const id = open ? projectId : undefined;

  const projectFinishDate = finishDate ? dayjs(finishDate).format('D MMM YYYY') : 'No finish date';
  const projectDuration = dayjs(finishDate).diff(dayjs(startDate), 'd') || 0;

  const editHandler = async (editProjectId: string) => {
    const { data } = await getProjectById(editProjectId);

    if (data) {
      dispatch(projectModalActions.openEditModal({ type: PROJECT_MODAL, data: mapProjectResponseToRequest(data) }));
    }
  };

  const ragIndicator = () => {
    const greyColor = theme.palette.busmanColors.lightGrey;
    const { rag, reason } = getRAGStatus(projectData);

    const circles = [
      { active: rag === 'GREEN', color: theme.palette.busmanColors.busmanGreen },
      { active: rag === 'AMBER', color: theme.palette.busmanColors.busmanWarn },
      { active: rag === 'RED', color: theme.palette.busmanColors.busmanRed },
    ];

    return (
      <Tooltip title={reason}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          {circles.map((c, i) => (
            <Box
              key={i}
              sx={{
                borderRadius: 100,
                background: c.active ? c.color : greyColor,
                width: 20,
                height: 20,
              }}
            />
          ))}
        </Box>
      </Tooltip>
    );
  };

  const renderProjectItemOption = () => {
    return (
      <Box
        sx={{
          p: 1,
          gap: 1,
          border: 0.5,
          width: '180px',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderColor: theme.palette.busmanColors.lightGrey,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            '&:hover': {
              cursor: 'pointer',
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => navigate(`/project/${projectId}`)}>
          <Typography variant="h6" sx={{ color: theme.palette.busmanColors.lightGrey }}>
            PROJECT OVERVIEW
          </Typography>
          <ArrowForwardIos sx={{ fontSize: 16 }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            '&:hover': {
              cursor: 'pointer',
              backgroundColor: 'action.hover',
            },
          }}
          onClick={() => editHandler(projectId)}>
          <Typography variant="h6" sx={{ color: theme.palette.busmanColors.lightGrey }}>
            EDIT PROJECT
          </Typography>
          <EditIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    );
  };

  return (
    <ProjectItemGrid
      container
      sx={{
        mb: 0.1,
        border: 1,
        height: '52px',
        display: 'flex',
        alignItems: 'center',
        borderColor: theme.palette.background.default,
      }}>
      <GridItem
        title={
          <FormControlLabel
            control={
              <Checkbox
                checked={true}
                checkedIcon={
                  <SquareRoundedIcon
                    sx={{
                      backgroundColor: theme.palette.background.default,
                      padding: '1px',
                      color: theme.palette.busmanColors.lightGrey,
                    }}
                  />
                }
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '& .MuiSvgIcon-root': {
                    width: '15px',
                    height: '15px',
                    color: theme.palette.busmanColors.lightGrey,
                    backgroundColor: theme.palette.background.default,
                    borderRadius: '3px',
                  },
                }}
              />
            }
            label={`${projectTitle}`}
            sx={{
              '& .MuiFormControlLabel-label': { fontWeight: 'bold' },
            }}
          />
        }
        breakpoint={3.5}
        seenOrIsHeading={false}
      />
      <>
        <GridItem
          title={
            <MoreVertIcon aria-describedby={id} sx={{ color: '#9e9e9e' }} onClick={(event) => handleClick(event)} />
          }
          seenOrIsHeading={false}
          breakpoint={0.5}
        />
        {open && (
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Popper id={id} open={open} anchorEl={anchorEl} placement={'right-end'}>
              {renderProjectItemOption()}
            </Popper>
          </ClickAwayListener>
        )}
      </>
      <GridItem title={`${clientName}`} seenOrIsHeading={false} breakpoint={1} />
      <GridItem title={`${projectFinishDate}`} seenOrIsHeading={false} breakpoint={1} />
      <GridItem title={`${projectDuration}d`} seenOrIsHeading={false} breakpoint={1} />
      <GridItem title={`${projectCost}`} seenOrIsHeading={false} breakpoint={1} />
      <GridItem title={`${projectStatus}`} seenOrIsHeading={false} breakpoint={1} />
      <GridItem
        title={
          <Box
            sx={{
              gap: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <BorderLinearProgress variant="determinate" value={Number(progressPercent)} sx={{ flex: 1 }} />
            <Typography>{progressPercent} %</Typography>
          </Box>
        }
        seenOrIsHeading={false}
        breakpoint={1.5}
      />
      <GridItem title={ragIndicator()} seenOrIsHeading={false} breakpoint={1} />
      <GridItem
        title={
          <ArrowForwardIos
            sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}
            onClick={() => navigate(`/project/${projectId}`)}
          />
        }
        seenOrIsHeading={false}
        breakpoint={0.5}
      />
    </ProjectItemGrid>
  );
};

export default ProjectLineItem;
